<template>
  <div class="ai-documents">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t("__aiDocumentManagement") }}</h2>

      <!-- 搜尋列 -->
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap">
        <!-- 左側：狀態篩選 + 搜尋框 -->
        <div class="d-flex align-items-center flex-wrap">
          <!-- 狀態篩選 -->
          <div class="device-report-select me-2 mb-2">
            <select v-model="filterStatus" class="form-select rounded-pill" @change="loadDocuments">
              <option value="">{{ $t("__allStatus") }}</option>
              <option value="pending">{{ $t("__aiDocStatusPending") }}</option>
              <option value="processing">{{ $t("__aiDocStatusProcessing") }}</option>
              <option value="completed">{{ $t("__aiDocStatusCompleted") }}</option>
              <option value="failed">{{ $t("__aiDocStatusFailed") }}</option>
            </select>
          </div>

          <!-- 搜尋框 -->
          <form @submit.prevent="loadDocuments" class="mb-2">
            <div class="input-group rounded-pill">
              <input
                v-model="search"
                type="text"
                class="form-control rounded-pill"
                :placeholder="$t('__aiDocSearchPlaceholder')"
                aria-label="search"
              />
              <button type="submit" class="btn btn-link px-2 text-dark">
                <i class="bi bi-search"></i>
              </button>
              <button
                v-if="search"
                type="button"
                class="btn btn-primary px-3 ms-1 rounded-pill"
                @click="clearSearch"
              >
                {{ $t('__clearSearch') }}
              </button>
            </div>
          </form>
        </div>

        <!-- 右側：類型選擇 + 上傳按鈕 -->
        <div class="d-flex align-items-center gap-2 mb-2">
          <select v-model="selectedTypeId" class="form-select rounded-pill" style="width: auto;">
            <option value="">{{ $t('__selectAiType') }}</option>
            <option v-for="type in types" :key="type.id" :value="type.id">{{ type.name }}</option>
          </select>
          <button
            @click="triggerUpload"
            class="btn btn-primary rounded-pill px-3"
            :disabled="uploading"
          >
            <i class="bi bi-cloud-upload me-1"></i>
            {{ $t('__aiDocUpload') }}
          </button>
          <input
            ref="fileInput"
            type="file"
            accept=".pdf,.docx,.txt"
            class="d-none"
            @change="handleFileSelect"
          />
        </div>
      </div>

      <!-- 上傳進度 -->
      <div v-if="uploading" class="alert alert-info d-flex align-items-center mb-3">
        <div class="spinner-border spinner-border-sm me-2" role="status"></div>
        <span>{{ $t('__aiDocUploading') }} {{ uploadFilename }} ({{ uploadProgress }}%)</span>
      </div>

      <!-- 背景處理中 -->
      <div v-if="processingDocName" class="alert alert-warning d-flex align-items-center mb-3">
        <div class="spinner-border spinner-border-sm me-2" role="status"></div>
        <span>{{ processingDocName }} {{ $t('__aiDocStatusProcessing') }}...</span>
      </div>

      <!-- 文件列表 -->
      <div class="table-responsive bg-white rounded shadow-sm">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th scope="col">{{ $t('__aiDocFilename') }}</th>
              <th scope="col" width="80">{{ $t('__aiDocFileType') }}</th>
              <th scope="col" width="100">{{ $t('__aiDocFileSize') }}</th>
              <th scope="col" width="120">{{ $t('__status') }}</th>
              <th scope="col" width="100">{{ $t('__aiDocKnowledgeCount') }}</th>
              <th scope="col" width="160">{{ $t('__aiDocUploadTime') }}</th>
              <th scope="col" width="140">{{ $t('__actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">{{ $t('__loading') }}</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="!documents.length">
              <td colspan="7" class="text-center py-5 text-muted">
                {{ $t('__noData') }}
              </td>
            </tr>
            <tr v-else v-for="doc in documents" :key="doc.id">
              <td>
                <span class="text-truncate d-inline-block" style="max-width: 300px;" :title="doc.original_filename || doc.filename">
                  {{ doc.original_filename || doc.filename }}
                </span>
              </td>
              <td>
                <span class="badge bg-secondary">{{ doc.file_type || getExtension(doc.filename) }}</span>
              </td>
              <td>{{ formatFileSize(doc.file_size) }}</td>
              <td>
                <span class="badge" :class="statusBadgeClass(doc.status)">
                  <span v-if="doc.status === 'pending' || doc.status === 'processing'" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  {{ statusLabel(doc.status) }}
                </span>
              </td>
              <td>{{ doc.knowledge_count || 0 }}</td>
              <td>{{ formatDate(doc.created_at) }}</td>
              <td>
                <button
                  class="btn btn-sm btn-outline-primary me-1"
                  @click="showDetail(doc)"
                  :title="$t('__view')"
                >
                  <i class="bi bi-eye"></i>
                </button>
                <button
                  v-if="doc.status === 'failed'"
                  class="btn btn-sm btn-outline-warning me-1"
                  @click="reprocessDoc(doc)"
                  :title="$t('__aiDocReprocess')"
                >
                  <i class="bi bi-arrow-clockwise"></i>
                </button>
                <button
                  class="btn btn-sm btn-outline-danger"
                  @click="deleteDoc(doc)"
                  :title="$t('__delete')"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分頁 -->
      <div v-if="pagination && pagination.last_page > 1" class="d-flex justify-content-center mt-3">
        <nav>
          <ul class="pagination">
            <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
              <a class="page-link" href="#" @click.prevent="goToPage(pagination.current_page - 1)">
                {{ $t('__previousPage') }}
              </a>
            </li>
            <li
              v-for="page in visiblePages"
              :key="page"
              class="page-item"
              :class="{ active: page === pagination.current_page }"
            >
              <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
              <a class="page-link" href="#" @click.prevent="goToPage(pagination.current_page + 1)">
                {{ $t('__nextPage') }}
              </a>
            </li>
          </ul>
        </nav>
      </div>

      <!-- 詳情 Modal -->
      <div class="modal fade" id="docDetailModal" tabindex="-1" ref="detailModal">
        <div class="modal-dialog modal-lg">
          <div class="modal-content" v-if="selectedDoc">
            <div class="modal-header">
              <h5 class="modal-title">{{ selectedDoc.original_filename || selectedDoc.filename }}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              <div class="row mb-3">
                <div class="col-md-6">
                  <p><strong>{{ $t('__aiDocFileType') }}:</strong> {{ selectedDoc.file_type }}</p>
                  <p><strong>{{ $t('__aiDocFileSize') }}:</strong> {{ formatFileSize(selectedDoc.file_size) }}</p>
                  <p><strong>{{ $t('__status') }}:</strong>
                    <span class="badge" :class="statusBadgeClass(selectedDoc.status)">{{ statusLabel(selectedDoc.status) }}</span>
                  </p>
                </div>
                <div class="col-md-6">
                  <p><strong>{{ $t('__aiDocKnowledgeCount') }}:</strong> {{ selectedDoc.knowledge_count || 0 }}</p>
                  <p><strong>{{ $t('__aiDocUploadTime') }}:</strong> {{ formatDate(selectedDoc.created_at) }}</p>
                  <p v-if="selectedDoc.processed_at"><strong>{{ $t('__aiDocProcessedAt') }}:</strong> {{ formatDate(selectedDoc.processed_at) }}</p>
                </div>
              </div>
              <div v-if="selectedDoc.error_message" class="alert alert-danger">
                <strong>{{ $t('__aiDocError') }}:</strong> {{ selectedDoc.error_message }}
              </div>
              <div v-if="selectedDoc.content">
                <h6>{{ $t('__aiDocContentPreview') }}</h6>
                <div class="border rounded p-3 bg-light" style="max-height: 300px; overflow-y: auto; white-space: pre-wrap;">{{ selectedDoc.content }}</div>
              </div>
              <div v-if="selectedDoc.knowledge_entries && selectedDoc.knowledge_entries.length" class="mt-3">
                <h6>{{ $t('__aiDocChunks') }} ({{ selectedDoc.knowledge_entries.length }})</h6>
                <div class="accordion" id="chunksAccordion">
                  <div v-for="(entry, idx) in selectedDoc.knowledge_entries" :key="entry.id" class="accordion-item">
                    <h2 class="accordion-header">
                      <button
                        class="accordion-button collapsed py-2"
                        type="button"
                        :data-bs-toggle="'collapse'"
                        :data-bs-target="'#chunk-' + idx"
                      >
                        {{ entry.title || ($t('__aiDocChunk') + ' ' + (idx + 1)) }}
                      </button>
                    </h2>
                    <div :id="'chunk-' + idx" class="accordion-collapse collapse">
                      <div class="accordion-body small" style="white-space: pre-wrap;">{{ entry.content }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('__close') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { aiDocument } from '@/http/api/aiDocument.js'
import { aiKnowledge } from '@/http/api/aiKnowledge.js'
import { Modal } from 'bootstrap'
import i18n from '@/lang/lang.js'

export default {
  name: 'AiDocuments',
  data () {
    return {
      documents: [],
      loading: false,
      uploading: false,
      uploadProgress: 0,
      uploadFilename: '',
      search: '',
      filterStatus: '',
      pagination: null,
      currentPage: 1,
      selectedDoc: null,
      bootstrap_modal: null,
      pollingTimers: {},
      processingDocName: '',
      types: [],
      selectedTypeId: ''
    }
  },
  computed: {
    ...mapState(['token']),
    visiblePages () {
      if (!this.pagination) return []
      const total = this.pagination.last_page
      const current = this.pagination.current_page
      const pages = []
      const start = Math.max(1, current - 2)
      const end = Math.min(total, current + 2)
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  mounted () {
    this.loadDocuments()
    this.loadTypes()
  },
  beforeDestroy () {
    this.clearAllPolling()
  },
  methods: {
    ...mapMutations(['Loading', 'Loaded', 'AlertsInfo', 'Alerted']),
    loadTypes () {
      const token = localStorage.getItem('humetrics_token')
      aiKnowledge.types.get(token)
        .then(res => {
          if (res.data && res.data.data) {
            this.types = res.data.data
          }
        })
        .catch(() => {})
    },
    loadDocuments () {
      this.loading = true
      const params = { page: this.currentPage, per_page: 20 }
      if (this.filterStatus) params.status = this.filterStatus
      if (this.search) params.search = this.search

      aiDocument.list(params, this.token)
        .then(res => {
          const data = res.data && res.data.data ? res.data.data : res.data
          if (data && data.data) {
            this.documents = data.data
            this.pagination = {
              current_page: data.current_page,
              last_page: data.last_page,
              total: data.total
            }
          } else {
            this.documents = []
            this.pagination = null
          }
          // 對 pending/processing 狀態的文件啟動輪詢
          this.documents.forEach(doc => {
            if (doc.status === 'pending' || doc.status === 'processing') {
              this.startPolling(doc.id)
            }
          })
        })
        .catch(() => {
          this.AlertsInfo({ state: 'error', title: i18n.t('__error'), info: this.$t('__aiGetDataFailed') })
          this.Alerted()
        })
        .finally(() => {
          this.loading = false
        })
    },
    clearSearch () {
      this.search = ''
      this.currentPage = 1
      this.loadDocuments()
    },
    goToPage (page) {
      if (page < 1 || (this.pagination && page > this.pagination.last_page)) return
      this.currentPage = page
      this.loadDocuments()
    },
    triggerUpload () {
      this.$refs.fileInput.click()
    },
    handleFileSelect (e) {
      const file = e.target.files[0]
      if (!file) return

      const maxSize = 20 * 1024 * 1024
      if (file.size > maxSize) {
        this.AlertsInfo({ state: 'error', title: i18n.t('__error'), info: this.$t('__aiDocFileTooLarge') })
        this.Alerted()
        this.$refs.fileInput.value = ''
        return
      }

      this.uploadFile(file)
      this.$refs.fileInput.value = ''
    },
    uploadFile (file) {
      this.uploading = true
      this.uploadProgress = 0
      this.uploadFilename = file.name

      const formData = new FormData()
      formData.append('file', file)
      if (this.selectedTypeId) {
        formData.append('type_id', this.selectedTypeId)
      }

      aiDocument.upload(formData, this.token, (progressEvent) => {
        if (progressEvent.total) {
          this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        }
      })
        .then(res => {
          if (res.data && res.data.status === 'success') {
            // 先設狀態 + 啟動輪詢（核心邏輯）
            this.processingDocName = this.uploadFilename
            this.loadDocuments()
            const doc = res.data.data && res.data.data.document
            if (doc && doc.id) {
              this.startPolling(doc.id)
            }
            // 最後顯示通知
            this.AlertsInfo({ state: 'success', title: i18n.t('__success'), info: this.$t('__aiDocUploadSuccess') })
            this.Alerted()
          } else {
            const msg = res.data && res.data.errMsg
            this.AlertsInfo({ state: 'error', title: i18n.t('__error'), info: msg || this.$t('__aiDocUploadFailed') })
            this.Alerted()
          }
        })
        .catch(() => {
          this.AlertsInfo({ state: 'error', title: i18n.t('__error'), info: this.$t('__aiDocUploadFailed') })
          this.Alerted()
        })
        .finally(() => {
          this.uploading = false
          this.uploadProgress = 0
          this.uploadFilename = ''
        })
    },
    startPolling (docId) {
      if (this.pollingTimers[docId]) return

      this.pollingTimers[docId] = setInterval(() => {
        aiDocument.status(docId, this.token)
          .then(res => {
            const statusData = res.data && res.data.data ? res.data.data : null
            if (!statusData) return

            // 更新列表中的文件狀態
            const idx = this.documents.findIndex(d => d.id === docId)
            if (idx !== -1) {
              this.$set(this.documents[idx], 'status', statusData.status)
              this.$set(this.documents[idx], 'knowledge_count', statusData.knowledge_count)
              this.$set(this.documents[idx], 'error_message', statusData.error_message)
            }

            // 完成或失敗時停止輪詢
            if (statusData.status === 'completed' || statusData.status === 'failed') {
              this.stopPolling(docId)
              this.processingDocName = ''
              if (statusData.status === 'completed') {
                this.AlertsInfo({ state: 'success', title: i18n.t('__success'), info: this.$t('__aiDocProcessComplete') })
              } else {
                this.AlertsInfo({ state: 'error', title: i18n.t('__error'), info: this.$t('__aiDocProcessFailed') })
              }
              this.Alerted()
            }
          })
          .catch(() => {
            this.stopPolling(docId)
          })
      }, 3000)
    },
    stopPolling (docId) {
      if (this.pollingTimers[docId]) {
        clearInterval(this.pollingTimers[docId])
        delete this.pollingTimers[docId]
      }
    },
    clearAllPolling () {
      Object.keys(this.pollingTimers).forEach(id => {
        clearInterval(this.pollingTimers[id])
      })
      this.pollingTimers = {}
    },
    showDetail (doc) {
      const vm = this
      aiDocument.show(doc.id, vm.token)
        .then(res => {
          if (res.data && res.data.status === 'success' && res.data.data) {
            vm.selectedDoc = res.data.data
          } else {
            vm.AlertsInfo({ state: 'error', title: i18n.t('__error'), info: vm.$t('__aiGetDataFailed') })
            vm.Alerted()
            return
          }
          vm.$nextTick(function () {
            vm.bootstrap_modal = new Modal(vm.$refs.detailModal)
            vm.bootstrap_modal.show()
          })
        })
        .catch(() => {
          vm.AlertsInfo({ state: 'error', title: i18n.t('__error'), info: vm.$t('__aiGetDataFailed') })
          vm.Alerted()
        })
    },
    reprocessDoc (doc) {
      if (!confirm(this.$t('__aiDocConfirmReprocess'))) return

      this.Loading()
      aiDocument.reprocess(doc.id, this.token)
        .then(res => {
          if (res.data && res.data.status === 'success') {
            this.AlertsInfo({ state: 'success', title: i18n.t('__success'), info: this.$t('__aiDocReprocessStarted') })
            this.Alerted()
            this.loadDocuments()
          } else {
            const msg = res.data && res.data.errMsg
            this.AlertsInfo({ state: 'error', title: i18n.t('__error'), info: msg || this.$t('__aiOperationFailed') })
            this.Alerted()
          }
          this.Loaded()
        })
        .catch(() => {
          this.AlertsInfo({ state: 'error', title: i18n.t('__error'), info: this.$t('__aiOperationFailed') })
          this.Alerted()
          this.Loaded()
        })
    },
    deleteDoc (doc) {
      const vm = this
      const name = doc.original_filename || doc.filename
      if (!confirm(vm.$t('__aiConfirmDelete', { title: name }))) return

      vm.Loading()
      aiDocument.destroy(doc.id, vm.token)
        .then(res => {
          if (res.data && res.data.status === 'success') {
            vm.stopPolling(doc.id)
            const idx = vm.documents.findIndex(d => d.id === doc.id)
            if (idx !== -1) vm.documents.splice(idx, 1)
            vm.AlertsInfo({ state: 'success', title: i18n.t('__success'), info: i18n.t('__deleteSuccess') })
            vm.Alerted()
          } else {
            const msg = res.data && res.data.errMsg
            vm.AlertsInfo({ state: 'error', title: i18n.t('__error'), info: msg || vm.$t('__aiDeleteFailed') })
            vm.Alerted()
          }
          vm.Loaded()
        })
        .catch(() => {
          vm.AlertsInfo({ state: 'error', title: i18n.t('__error'), info: vm.$t('__aiDeleteFailed') })
          vm.Alerted()
          vm.Loaded()
        })
    },
    statusBadgeClass (status) {
      const map = {
        pending: 'bg-secondary',
        processing: 'bg-primary',
        completed: 'bg-success',
        failed: 'bg-danger'
      }
      return map[status] || 'bg-secondary'
    },
    statusLabel (status) {
      const map = {
        pending: this.$t('__aiDocStatusPending'),
        processing: this.$t('__aiDocStatusProcessing'),
        completed: this.$t('__aiDocStatusCompleted'),
        failed: this.$t('__aiDocStatusFailed')
      }
      return map[status] || status
    },
    formatFileSize (bytes) {
      if (!bytes) return '-'
      const units = ['B', 'KB', 'MB', 'GB']
      let i = 0
      let size = bytes
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024
        i++
      }
      return size.toFixed(1) + ' ' + units[i]
    },
    formatDate (dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    getExtension (filename) {
      if (!filename) return ''
      return filename.split('.').pop()
    }
  }
}
</script>

<style scoped>
.ai-documents .table th {
  font-weight: 600;
  font-size: 0.875rem;
  color: #6c757d;
  border-top: none;
}
.ai-documents .table td {
  vertical-align: middle;
}
</style>
